import React, { useCallback, useMemo } from "react";
import { Text, Flex, IconButton } from "@radix-ui/themes";
import { useMediaQuery } from "react-responsive";
import {
  ChevronDownIcon,
  Cross1Icon,
  DotsVerticalIcon,
  ExitIcon,
  MinusIcon,
  ReloadIcon,
} from "@radix-ui/react-icons";
import { DropdownMenu } from "./DropdownMenu";
import { MinimizeIcon } from "../entities/ChatSettingsV2";

interface HeaderProps {
  tenant: string;
  name: string;
  quackAgentName: string;
  agents?: string[];
  allowNewChat?: boolean;
  allowCloseChat?: boolean;
  onClose?: () => void;
  onStartNewChat?: () => Promise<void>;
  onCloseChat?: () => Promise<void>;
  minimizeIcon?: MinimizeIcon;
}

export function Header({
  tenant,
  name,
  quackAgentName,
  agents = [],
  allowNewChat,
  allowCloseChat,
  onClose,
  onStartNewChat,
  onCloseChat,
  minimizeIcon,
}: HeaderProps) {
  const isSmallScreen = useMediaQuery({ query: "(max-width: 520px)" });

  const hasNewChatButton = useMemo(() => {
    if (allowNewChat !== undefined) {
      return allowNewChat;
    }

    return tenant.startsWith("yotpo") || tenant === "faye" || tenant === "dremio";
  }, [tenant, allowNewChat]);

  const hasCloseChatButton = useMemo(() => {
    if (allowCloseChat !== undefined) {
      return allowCloseChat;
    }

    return tenant.startsWith("yotpo");
  }, [tenant, allowCloseChat]);

  const handleCloseChat = useCallback(async () => {
    await onCloseChat?.();
  }, [onCloseChat]);

  const handleStartNewChat = useCallback(async () => {
    await onStartNewChat?.();
  }, [onStartNewChat, tenant]);

  return (
    <Flex py={"2"} px={"4"} gap={"6"} align={"center"}>
      <Flex flexGrow={"1"} direction={"column"}>
        <Text size={"3"} color={"gray"} weight={"medium"} highContrast>
          {name}
        </Text>

        <Text size={"2"} color={"gray"}>
          {agents.length > 0 ? agents.join(", ") : quackAgentName}
        </Text>
      </Flex>

      <Flex gap={"4"}>
        {hasNewChatButton && (
          <IconButton
            variant="ghost"
            color="gray"
            onClick={handleStartNewChat}
            aria-label="Start new chat"
          >
            <ReloadIcon width="15" height="15" />
          </IconButton>
        )}

        {hasCloseChatButton && (
          <DropdownMenu
            items={[
              {
                label: "Close chat",
                onClick: handleCloseChat,
                icon: <ExitIcon />,
              },
            ]}
          >
            <IconButton variant="ghost" color="gray" aria-label="Toggle menu actions">
              <DotsVerticalIcon />
            </IconButton>
          </DropdownMenu>
        )}

        <IconButton variant="ghost" color="gray" onClick={onClose} aria-label="Close chat">
          {isSmallScreen ? (
            <Cross1Icon width="15" height="15" />
          ) : minimizeIcon === "dash" ? (
            <MinusIcon width="15" height="15" />
          ) : (
            <ChevronDownIcon width="15" height="15" />
          )}
        </IconButton>
      </Flex>
    </Flex>
  );
}
